import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

function getCroppedDataUrl(imageSrc, area, size = 480) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, size, size);
      // JPEG compression to keep payload small
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
}

export default function PhotoCropDialog({ open, src, onClose, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_a, areaPixels) => setAreaPx(areaPixels), []);

  const handleConfirm = async () => {
    if (!areaPx) return;
    setBusy(true);
    try {
      const dataUrl = await getCroppedDataUrl(src, areaPx);
      await onConfirm(dataUrl);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="heading-3">Crop Your Photo</DialogTitle>
          <DialogDescription className="text-ink-300">Drag to position, use the slider to zoom, then save.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-72 bg-black rounded-md overflow-hidden">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="px-1">
          <p className="eyebrow text-[10px] mb-2">Zoom</p>
          <Slider
            value={[zoom]}
            min={1}
            max={3}
            step={0.05}
            onValueChange={(v) => setZoom(v[0])}
            data-testid="photo-zoom-slider"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 border-ink-700 hover:bg-ink-800" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={busy} className="flex-1 bg-burgundy hover:bg-burgundy-hover" data-testid="photo-confirm-btn">
            {busy ? "Uploading…" : "Save Photo"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
